import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';

import DeleteModal from '@atom/components/common/DeleteModal';
import UserGroupIcon from '@atom/components/common/UserGroupIcon';
import InheritanceLockIcon from '@atom/components/workTemplate/InheritanceLockIcon';
import WorkTemplateContext from '@atom/components/workTemplate/WorkTemplateContext';
import { WORK_TEMPLATE_TASK_USER_GROUP_DELETE } from '@atom/graph/workTemplate';
import { Icon, IconButton } from '@atom/mui';
import { CloseIcon } from '@atom/mui/icons';
import {
  InheritedComponentType,
  WorkTemplateTaskUserGroup,
} from '@atom/types/workTemplate';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { isComponentInherited } from '@atom/utilities/workTemplateUtilities';

import EditTaskUserGroupModal from './EditTaskUserGroupModal';

import classes from './teamSection.module.css';

interface Props {
  userGroup: WorkTemplateTaskUserGroup;
}

const TaskUserGroupTile = ({ userGroup }: Props) => {
  const { workTemplate, task, refetch } = useContext(WorkTemplateContext);

  const [open, setOpen] = useState<boolean>(false);
  const [removeModalOpen, setRemoveModalOpen] = useState<boolean>(false);

  const [taskUserGroupRemove] = useMutation(
    WORK_TEMPLATE_TASK_USER_GROUP_DELETE,
    {
      onCompleted: () => {
        refetch();
      },
    },
  );

  const removeUserGroup = async () => {
    await taskUserGroupRemove({
      variables: {
        input: {
          taskId: task.id,
          userGroupId: userGroup.userGroupId,
          workTemplateId: workTemplate.id,
        },
      },
    });

    setRemoveModalOpen(false);
    refetch();
  };

  const usersLabel = userGroup.quantity === 1 ? 'User' : 'Users';

  const isDisabled =
    workTemplate?.published || doesNotHaveRolePermissions(ROLE_SETS.ORG_ADMIN);

  const isItemInherited = isComponentInherited(
    InheritedComponentType.TASK_USER_GROUP,
    task?.inheritedComponents || [],
    userGroup?.userGroupId,
  );

  const actionButton = isItemInherited ? (
    <div className={classes.lockContainer}>
      <InheritanceLockIcon
        tooltip="Inherited Group cannot be removed or edited."
        placement="bottom-start"
      />
    </div>
  ) : isDisabled ? (
    <div />
  ) : (
    <IconButton
      onClick={() => setRemoveModalOpen(true)}
      tooltip="Remove"
      edge="end"
      disabled={isDisabled}
    >
      <Icon icon={CloseIcon} />
    </IconButton>
  );

  return (
    <>
      <div className={clsx(classes.userContainer, classes.group)}>
        <div
          className={classes.userContentContainer}
          onClick={() => setOpen(!isDisabled)}
        >
          <UserGroupIcon colorId={userGroup.colorId} />
          <div className={classes.userDetails}>
            <div className={classes.name}>{userGroup.name}</div>
            <div className={classes.email}>
              <div className={classes.email}>
                <span>{`${userGroup.userCount} ${usersLabel}`}</span>
              </div>
            </div>
          </div>
        </div>
        {actionButton}
      </div>
      <EditTaskUserGroupModal
        open={open}
        onClose={() => setOpen(false)}
        taskUserGroup={userGroup}
      />
      <DeleteModal
        open={removeModalOpen}
        onCancel={() => setRemoveModalOpen(false)}
        onConfirm={removeUserGroup}
        title="Remove User Group From Task"
        confirmText="Remove"
        content="Are you sure you want to remove this user group from the task?"
      />
    </>
  );
};

export default TaskUserGroupTile;
